import { useState } from 'react'
import { LoadingSkeleton } from 'components'
import ButtonWithConfirmationDialog from 'components/ButtonWithConfirmationDialog/ButtonWithConfirmationDialog'
import SyncProgressBar from 'components/SyncProgressBar/SyncProgressBar'
import { FormValueTypes } from 'types'
import { useGetMenuImportStatuses, useSyncLifecycle } from '../../hooks'
import MenuImportRow from './MenuImportRow'
import MenuImportHistoryModal from 'components/MenuImportHistoryModal/MenuImportHistoryModal'
import * as Styled from './style'

type Props = {
  resyncSumbit: () => void
  upCharge: number | string
  onSubmit: (value: FormValueTypes) => Promise<void>
  locationId: number
  isPrinterClient?: boolean
}

const MenuImport = ({ resyncSumbit, locationId, isPrinterClient }: Props) => {
  const [{ menuImportStatuses, isLoading }] = useGetMenuImportStatuses(locationId?.toString(), 4)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const openModal = () => setIsModalOpen(true)
  const closeModal = () => setIsModalOpen(false)

  // Get partner ID for sync lifecycle API
  const partnerId = locationId?.toString() || null
  
  const { 
    progressSteps, 
    isSyncing, 
    elapsedTime,
    isLoading: isLoadingSync,
    error: syncError
    // Note: activeSyncStatus and allSyncStatuses available for future features
  } = useSyncLifecycle(partnerId)
 
  const handleResync = () => {
    // TODO: Add polling or implement Pusher real-time updates for live sync progress
    
    // Call original resync function to start the actual sync process
    resyncSumbit()
  }

  return (
    <Styled.MenuImportContainer>
      <Styled.Overlay />
      <Styled.MenuImportHeader>
        <Styled.TitleGroup>
          <Styled.Title>Menu Import Status</Styled.Title>
          <Styled.ViewHistoryLink onClick={openModal}>View History</Styled.ViewHistoryLink>
        </Styled.TitleGroup>

        <ButtonWithConfirmationDialog
          title={isPrinterClient ? 'Publish' : 'Resync'}
          description={`We’re retrieving your latest POS menu(s) and updating your ordering platforms. While this action is immediate, depending on the size of your menu(s), it may take up to an hour for your menus to publish across all platforms.`}
          okText='Resync Menus'
          isDisabled={!menuImportStatuses || isLoading}
          isDanger={false}
          onOk={handleResync}
        />
      </Styled.MenuImportHeader>
      
      {/* Show sync error if API call failed */}
      {syncError && (
        <Styled.ErrorMessage>
          ⚠️ {syncError}
        </Styled.ErrorMessage>
      )}
      
      {isLoading ? (
        <LoadingSkeleton isFlex lineCount={5} />
      ) : (
        <Styled.MenuImportWrapper>
          {/* Show sync progress bar for active syncs only (completed state will come with Pusher) */}
          {!isLoadingSync && !syncError && (isSyncing || progressSteps.length > 0) && (
            <SyncProgressBar
              progress={progressSteps}
              isSyncing={isSyncing}
              elapsedTime={elapsedTime}
            />
          )}
          <Styled.RowsContainer>
            {menuImportStatuses?.map((item) => (
              <MenuImportRow key={item.id} item={item} />
            ))}
          </Styled.RowsContainer>
        </Styled.MenuImportWrapper>
      )}

      {isModalOpen && (
        <MenuImportHistoryModal locationId={locationId} isOpen={isModalOpen} onClose={closeModal} />
      )}
    </Styled.MenuImportContainer>
  )
}

export default MenuImport
